import { useState, useEffect, Suspense, lazy } from "react";
import { api } from "../../api";
// Reuse the audio/video sections' row styling (hv-* classes) so all three
// home sections share one list design.
import "../videolectures/videoSection.css";

// The document reader pulls in the flipbook + PDF renderer, so keep it out of
// the home bundle until a wazifa is actually opened.
const DocumentReader = lazy(() => import("../../pages/library/DocumentReader"));

const PAGE_SIZE = 3;

// Maps a backend LibraryItem → the shape this list renders and DocumentReader
// consumes (it reads `id` and `type`). Mirrors mapLibraryItem in Library.jsx.
function mapLibraryItem(item, idx) {
  return {
    id: item?._id || String(idx + 1),
    en: item?.titleEn || item?.title || "",
    ar: item?.titleUr || item?.titleUrdu || "",
    type: item?.fileType, // "pdf" | "image"
  };
}

async function fetchWazaif() {
  const { data } = await api.get(`/library/categories/wazaif/items`, {
    params: { limit: PAGE_SIZE, _: Date.now() },
  });
  if (data?.success !== true) throw new Error(data?.message || "Request failed");
  return (data?.data || []).map(mapLibraryItem);
}

export default function WazaifSection() {
  const [items, setItems] = useState([]);
  const [openDoc, setOpenDoc] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const wazaif = await fetchWazaif();
        if (!alive) return;
        setItems(wazaif.slice(0, PAGE_SIZE));
      } catch (err) {
        console.error("Wazaif fetch failed:", err);
        if (!alive) return;
        setItems([]);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  return (
    // home-video provides the shared section frame (padding, fonts) used by
    // the audio and video sections, so title and gutters line up exactly.
    <section className="home-video bg-white text-black">
      <div className="hv-head">
        <h2 className="hv-title">Wazaif</h2>
        <a className="hv-viewall" href="/library">
          View All
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M5 12h14M13 6l6 6-6 6" />
          </svg>
        </a>
      </div>

      <div className="flex flex-col lg:flex-row gap-8 lg:gap-10 items-stretch w-full">
        <div className="w-full lg:w-1/2 flex">
          <div className="bg-gray-100 rounded-2xl paddingWazaif py-4 flex flex-row flex-wrap items-center gap-3 shadow w-full h-full">
            <div className="relative w-32 h-32 mb-[-50px] sm:h-[200px] sm:w-[180px] flex-shrink-0 overflow-visible text-center">
              <img
                src="/images/quranTasbeehImge.png"
                alt="Quran with beads"
                className="
    w-[110px] sm:w-[150px]
    h-auto
    object-contain
    mx-auto
    flex-shrink-0
  "
              />
            </div>

            <div className="text-left flex-1 min-w-0">
              <p className="text-[14px] md:text-[24px] font-arabic md:mb-3 font-normal text-gray-800 leading-relaxed text-right">
                ٱلَّذِينَ ءَامَنُوا۟ وَتَطْمَئِنُّ قُلُوبُهُم بِذِكْرِ ٱللَّهِ ۗ
                أَلَا بِذِكْرِ ٱللَّهِ تَطْمَئِنُّ ٱلْقُلُوبُ ٢٨
              </p>
              <p className="text-[14px] leading-none pb-0 mb-0 text-gray-900 font-arabic text-left">
                Those who believe and whose hearts find comfort in the
                remembrance of Allah. Surely in the remembrance of Allah do
                hearts find comfort.
              </p>
            </div>
          </div>
        </div>

        {/* mobile shows the first 2 rows, like the audio/video sections */}
        <ul className="hv-list w-full md:w-1/2">
          {(isMobile ? items.slice(0, 2) : items).map((doc, idx) => (
            <li
              key={doc.id}
              className="hv-row"
              onClick={() => setOpenDoc(doc)}
            >
              <span className="hv-num">
                {(idx + 1).toString().padStart(2, "0")}
              </span>
              <div className="hv-row-main">
                <p
                  className={`hv-row-title ${doc.ar ? "hv-row-title--ar" : ""}`}
                  dir="rtl"
                  style={{ textAlign: "left" }}
                >
                  {doc.ar || doc.en}
                </p>
                {doc.ar && doc.en && (
                  <p className="hv-row-title-en">{doc.en}</p>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>

      {openDoc && (
        <Suspense fallback={null}>
          <DocumentReader item={openDoc} onClose={() => setOpenDoc(null)} />
        </Suspense>
      )}
    </section>
  );
}
